import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_LOCATION_COORDS } from 'config/defaultLocation.config';
import {
  addAlertPoint,
  moveAlertPoint,
} from '../../features/AlertMapEditor/utils/alertMapEditor.utils';
import {
  ALERT_MODES,
  clearCoordinatesFromAlertData,
  createInitialIpawsAlertDraft,
  createInitialRescAlertDraft,
  removeLastCoordinateFromAlertData,
} from '../../utils/alertCreator.utils';
import { toFemaEnvironmentClass } from '../../utils/femaEnvironment.utils';
import {
  getIpawsAreaMetrics,
  getIpawsAreaShapes,
  IPAWS_AREA_LIMITS,
  IPAWS_AREA_TYPES,
} from '../../utils/ipawsArea.utils';

const defaultCoordinates = [...DEFAULT_LOCATION_COORDS];

const getActiveAlertDraft = (state) => state.drafts[state.alertMode];

/**
 * The Alert Creator's IPAWS draft. `femaEnvironment` is the FEMA environment select's class token
 * (card FE-01; multi-COG contract §1). The CAP payload builders never read it. A new send carries
 * it as the multipart field `environment` beside `data`. It is not in
 * `createInitialIpawsAlertDraft`, because the Global Actions editor saves that draft to its service.
 * `null` means "use the default class of `GET /fusion/v2/fema/meta`".
 * @returns {object} A new IPAWS draft for this slice.
 */
const createAlertCreatorIpawsDraft = () => ({
  ...createInitialIpawsAlertDraft(),
  femaEnvironment: null,
});

/**
 * Gives every IPAWS shape action the current shape list.
 * @param {object} state The editable Alert Creator state.
 * @returns {object[]} The IPAWS draft's current shape list.
 */
const ensureIpawsAreaShapes = (state) => {
  const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
  ipawsDraft.areaShapes = getIpawsAreaShapes(ipawsDraft);
  return ipawsDraft.areaShapes;
};

/**
 * Checks a possible shape list before a reducer saves it.
 * @param {object[]} areaShapes The list after the proposed change.
 * @returns {boolean} Whether the list still fits the editor's shape and point limits.
 */
const isWithinIpawsAreaLimits = (areaShapes) => {
  const { shapeCount, pointCount } = getIpawsAreaMetrics(areaShapes);

  return (
    shapeCount <= IPAWS_AREA_LIMITS.maximumShapeCount &&
    pointCount <= IPAWS_AREA_LIMITS.maximumPointCount
  );
};

// The state helper safely turns the direct-looking changes below into a new stored state value.
const alertCreatorSlice = createSlice({
  name: 'alertCreator',
  initialState: {
    alertMode: ALERT_MODES.RESCALERT,
    drafts: {
      [ALERT_MODES.RESCALERT]: createInitialRescAlertDraft(),
      [ALERT_MODES.IPAWS]: createAlertCreatorIpawsDraft(),
    },
    creatorConfig: {
      timeInputMode: 'setTime',
      defaultCoordinates: defaultCoordinates,
      mapCenter: defaultCoordinates,
      zoom: 13,
      getMapCenter: {
        lat: defaultCoordinates[0],
        lng: defaultCoordinates[1],
      },
    },
    // FE-03 (2026-08-26): raised before a FEMA submit, released by the HTTP answer OR the
    // workplace's `ipaws-message-update` event — whichever lands first.
    ipawsHoldFlag: false,
    // The CAP identifier of this screen's last submission: the event toasts only its own message.
    ipawsLastCapIdentifier: null,
    femaUpdateRequest: null,
    femaUpdateContext: null,
    rescAlertUpdateContext: null,
  },
  reducers: {
    setRescAlertDraft: (state, action) => {
      state.drafts[ALERT_MODES.RESCALERT] = action.payload;
    },
    updateActiveAlertDraft: (state, action) => {
      Object.assign(getActiveAlertDraft(state), action.payload);
    },
    setAlertMode: (state, action) => {
      state.alertMode = action.payload;
    },
    setAlertCreatorConfig: (state, action) => {
      state.creatorConfig = {
        ...state.creatorConfig,
        ...action.payload,
      };
    },
    setMapCenter: (state, action) => {
      state.creatorConfig.mapCenter = action.payload;
    },
    setGetMapCenter: (state, action) => {
      state.creatorConfig.getMapCenter = action.payload;
    },
    setZoom: (state, action) => {
      state.creatorConfig.zoom = action.payload;
    },
    setTimeInputMode: (state, action) => {
      state.creatorConfig.timeInputMode = action.payload;
    },
    addAlertMarker: (state, action) => {
      const latlng = action.payload;
      if (!latlng) return;
      const rescAlertDraft = state.drafts[ALERT_MODES.RESCALERT];
      Object.assign(rescAlertDraft, addAlertPoint(rescAlertDraft, latlng));
    },
    moveAlertMarker: (state, action) => {
      const { index, newPosition } = action.payload;
      if (typeof index !== 'number' || !newPosition) return;
      const rescAlertDraft = state.drafts[ALERT_MODES.RESCALERT];
      Object.assign(rescAlertDraft, moveAlertPoint(rescAlertDraft, index, newPosition));
    },
    removeLastCoordinate: (state) => {
      const rescAlertDraft = state.drafts[ALERT_MODES.RESCALERT];
      Object.assign(rescAlertDraft, removeLastCoordinateFromAlertData(rescAlertDraft));
    },

    clearCoordinates: (state) => {
      const rescAlertDraft = state.drafts[ALERT_MODES.RESCALERT];
      Object.assign(rescAlertDraft, clearCoordinatesFromAlertData(rescAlertDraft));
    },
    // Adds one complete shape. IDs let selection and later edits target the exact same shape.
    addIpawsAreaShape: (state, action) => {
      const shape = action.payload;
      const supportedTypes = Object.values(IPAWS_AREA_TYPES);
      if (!shape?.id || !supportedTypes.includes(shape.type)) return;

      const areaShapes = ensureIpawsAreaShapes(state);
      if (areaShapes.some((candidate) => candidate.id === shape.id)) return;

      // Check a copy first so a rejected addition never changes the saved draft.
      const nextAreaShapes = [...areaShapes, shape];
      if (!isWithinIpawsAreaLimits(nextAreaShapes)) return;

      areaShapes.push(shape);
    },
    // Adds one click to a polygon and also remembers the order in which clicks were added.
    addIpawsPolygonVertex: (state, action) => {
      const { areaId, coordinate } = action.payload ?? {};
      if (!areaId || !coordinate) return;

      const areaShapes = ensureIpawsAreaShapes(state);
      const shape = areaShapes.find((candidate) => candidate.id === areaId);
      if (shape?.type !== IPAWS_AREA_TYPES.POLYGON) return;

      const updatedShape = {
        ...shape,
        coordinates: [...(shape.coordinates ?? []), coordinate],
        // Keep a separate record of which point was added last. The move action below updates this
        // record after a drag, so Undo can still find the same point at its new position.
        lastInserted: [...(shape.lastInserted ?? []), coordinate],
      };
      const nextAreaShapes = areaShapes.map((candidate) =>
        candidate.id === areaId ? updatedShape : candidate,
      );
      if (!isWithinIpawsAreaLimits(nextAreaShapes)) return;

      shape.coordinates = updatedShape.coordinates;
      shape.lastInserted = updatedShape.lastInserted;
    },
    // Moves one polygon point without changing the order used by Undo.
    moveIpawsPolygonVertex: (state, action) => {
      const { areaId, vertexIndex, newPosition } = action.payload ?? {};
      if (!areaId || !Number.isInteger(vertexIndex) || !newPosition) return;

      const shape = ensureIpawsAreaShapes(state).find((candidate) => candidate.id === areaId);
      if (
        shape?.type !== IPAWS_AREA_TYPES.POLYGON ||
        vertexIndex < 0 ||
        vertexIndex >= (shape.coordinates?.length ?? 0)
      ) {
        return;
      }

      const previousPosition = shape.coordinates[vertexIndex];
      shape.coordinates[vertexIndex] = newPosition;

      // Update the matching entry in the Undo list too. Replace only one match because a polygon
      // can contain two points with the same coordinates while it is still being edited.
      let hasReplacedPreviousPosition = false;
      shape.lastInserted = (shape.lastInserted ?? []).map((coordinate) => {
        if (
          !hasReplacedPreviousPosition &&
          coordinate?.lat === previousPosition?.lat &&
          coordinate?.lng === previousPosition?.lng
        ) {
          hasReplacedPreviousPosition = true;
          return newPosition;
        }

        return coordinate;
      });
    },
    // Updates only the fields that a square or circle editor is allowed to change.
    updateIpawsAreaShape: (state, action) => {
      const { areaId, updates } = action.payload ?? {};
      if (!areaId || !updates) return;

      const areaShapes = ensureIpawsAreaShapes(state);
      const shape = areaShapes.find((candidate) => candidate.id === areaId);
      if (!shape) return;

      const updatedShape = { ...shape };
      // Names belong to the editor only; geometry and CAP area data stay unchanged.
      if (typeof updates.name === 'string') updatedShape.name = updates.name;
      if (updates.center) updatedShape.center = updates.center;
      if (Array.isArray(updates.coordinates)) updatedShape.coordinates = updates.coordinates;
      if (Array.isArray(updates.lastInserted)) updatedShape.lastInserted = updates.lastInserted;
      // Check whether the field was sent instead of whether it is truthy. This allows a typed zero
      // to reach normal validation and show a useful size error to the user.
      if (Object.prototype.hasOwnProperty.call(updates, 'radiusKm')) {
        updatedShape.radiusKm = updates.radiusKm;
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'sideLengthKm')) {
        updatedShape.sideLengthKm = updates.sideLengthKm;
      }

      const nextAreaShapes = areaShapes.map((candidate) =>
        candidate.id === areaId ? updatedShape : candidate,
      );
      if (!isWithinIpawsAreaLimits(nextAreaShapes)) return;

      Object.assign(shape, updatedShape);
    },
    removeIpawsAreaShape: (state, action) => {
      const areaId = action.payload;
      if (!areaId) return;

      const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
      const areaShapes = ensureIpawsAreaShapes(state);
      ipawsDraft.areaShapes = areaShapes.filter((shape) => shape.id !== areaId);
    },
    clearIpawsAreaShapes: (state) => {
      const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
      ipawsDraft.areaShapes = [];
    },
    // The chosen county is the target, by its SAME geocode. Its boundary is kept for the map
    // only and never becomes a shape: a Census ring has hundreds of points (CAP allows 100 in
    // total), and the ring the jurisdictions list serves is buffered by 1000 ft. Shapes drawn on
    // the map narrow the area and are left alone here.
    applyIpawsResolvedJurisdiction: (state, action) => {
      const { jurisdiction, sameGeocode } = action.payload ?? {};
      if (!jurisdiction || !sameGeocode) return;
      const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
      ipawsDraft.resolvedJurisdiction = jurisdiction;
      ipawsDraft.geocodes = [{ valueName: 'SAME', value: sameGeocode }];
      if (!ipawsDraft.areaDesc) ipawsDraft.areaDesc = jurisdiction.name;
    },
    clearIpawsResolvedJurisdiction: (state) => {
      const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
      ipawsDraft.resolvedJurisdiction = null;
      ipawsDraft.geocodes = [];
    },
    // The FEMA environment select (card FE-01; multi-COG contract §1). A change from one class to
    // another clears the resolved jurisdiction and the geocodes: each class has its own COG
    // profile, so an area of the old class can be outside the new COG. The first default
    // (from `null`) clears nothing.
    setIpawsFemaEnvironment: (state, action) => {
      const ipawsDraft = state.drafts[ALERT_MODES.IPAWS];
      const next = action.payload ?? null;
      const previous = ipawsDraft.femaEnvironment ?? null;
      if (previous !== null && next !== previous) {
        ipawsDraft.resolvedJurisdiction = null;
        ipawsDraft.geocodes = [];
      }
      ipawsDraft.femaEnvironment = next;
    },
    setIpawsHoldFlag: (state, action) => {
      state.ipawsHoldFlag = Boolean(action.payload);
    },
    setIpawsLastCapIdentifier: (state, action) => {
      state.ipawsLastCapIdentifier = action.payload ?? null;
    },
    // An Update goes to the class of the original message (multi-COG contract §1). The record's
    // `environment` is `null` for a row written before migration 010: the draft class then stays
    // as it is, and the backend resolves the class from the stored row.
    setFemaUpdateContext: (state, action) => {
      state.femaUpdateContext = action.payload ?? null;
      state.femaUpdateRequest = null;
      const recordClass = toFemaEnvironmentClass(action.payload?.environment);
      if (recordClass) state.drafts[ALERT_MODES.IPAWS].femaEnvironment = recordClass;
    },
    setFemaUpdateRequest: (state, action) => {
      state.femaUpdateRequest = action.payload ?? null;
      state.femaUpdateContext = null;
      state.alertMode = ALERT_MODES.IPAWS;
    },
    setRescAlertUpdateContext: (state, action) => {
      const { alert, draft } = action.payload ?? {};
      state.rescAlertUpdateContext = alert ?? null;
      if (draft) state.drafts[ALERT_MODES.RESCALERT] = draft;
      state.alertMode = ALERT_MODES.RESCALERT;
    },
    clearRescAlertUpdateContext: (state) => {
      state.rescAlertUpdateContext = null;
    },
    // Leaving an Update gives the select back its default class, so that the class of the
    // original message (possibly production) does not stay chosen for a new alert.
    clearFemaUpdateContext: (state) => {
      if (state.femaUpdateContext) state.drafts[ALERT_MODES.IPAWS].femaEnvironment = null;
      state.femaUpdateContext = null;
      state.femaUpdateRequest = null;
    },
    // Wipes IPAWS-specific draft and context so a later visit never reuses stale IPAWS data.
    resetIpawsCreator: (state) => {
      state.alertMode = ALERT_MODES.RESCALERT;
      state.drafts[ALERT_MODES.IPAWS] = createAlertCreatorIpawsDraft();
      state.ipawsHoldFlag = false;
      state.ipawsLastCapIdentifier = null;
      state.femaUpdateRequest = null;
      state.femaUpdateContext = null;
      state.rescAlertUpdateContext = null;
    },
  },
  extraReducers: (builder) => {},
});

export const {
  setRescAlertDraft,
  updateActiveAlertDraft,
  setAlertMode,
  setAlertCreatorConfig,
  setMapCenter,
  setGetMapCenter,
  setZoom,
  setTimeInputMode,
  removeLastCoordinate,
  clearCoordinates,
  addAlertMarker,
  moveAlertMarker,
  addIpawsAreaShape,
  addIpawsPolygonVertex,
  moveIpawsPolygonVertex,
  updateIpawsAreaShape,
  removeIpawsAreaShape,
  clearIpawsAreaShapes,
  applyIpawsResolvedJurisdiction,
  clearIpawsResolvedJurisdiction,
  setIpawsFemaEnvironment,
  setIpawsHoldFlag,
  setIpawsLastCapIdentifier,
  setFemaUpdateRequest,
  setFemaUpdateContext,
  setRescAlertUpdateContext,
  clearRescAlertUpdateContext,
  clearFemaUpdateContext,
  resetIpawsCreator,
} = alertCreatorSlice.actions;

export default alertCreatorSlice.reducer;
